import { useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import BrandName from "../components/BrandName";
import Button from "../components/Button";
import Input from "../components/Input";
import ErrorMessage from "../components/ErrorMessage";
import GoogleSignInButton from "../components/GoogleSignInButton";
import SupportLink from "../components/SupportLink";
import ShareInvite from "../components/ShareInvite";
import TwoFactorPrompt from "../components/TwoFactorPrompt";
import { login, loginWithGoogle } from "../services/authService";
import {
  restoreOnboardingFromServer,
  syncInstitutionsFromServer,
} from "../services/onboardingService";
import "../styles/onboarding.css";
import "../styles/login.css";

/*
  LoginPage — מסך כניסה למנוי רשום (UI_SPEC ס' 2): שם משתמש/מייל + סיסמה.
  מתחבר ל-`POST /api/auth/login`, שומר את ה-token וממשיך פנימה.
  "כניסה עם Google" עדיין מושבתת — תופעל כשייבנה זרם ה-OAuth בשרת.
*/
function LoginPage() {
  const navigate = useNavigate();
  // יעד לחזרה אחרי כניסה (למשל השלמת הצטרפות לגן: ?next=/join/<token>).
  // מתירים רק נתיבים פנימיים (מתחילים ב-"/") כדי למנוע הפניה החוצה.
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : null;
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /*
    אתגר אימות דו-שלבי פתוח. כשהוא קיים, הסיסמה כבר אומתה אבל הכניסה **טרם
    הושלמה ואין טוקן** — ולכן מציגים את מסך הקוד במקום טופס הכניסה.
  */
  const [challenge, setChallenge] = useState(null);

  /*
    מה שקורה אחרי כניסה מוצלחת — משותף לשלושת המסלולים (סיסמה, Google, וקוד
    דו-שלבי), כדי ששלב שני לא יעקוף את שחזור הגן והמוסדות.
  */
  const finishLogin = useCallback(async () => {
    // משחזר את הגדרת הגן מהשרת (אם המטמון המקומי נוקה בהחלפת משתמש)
    await restoreOnboardingFromServer();
    // מסנכרן את כל הגנים (כולל כאלה שהוזמנת אליהם) למחליף המוסדות
    await syncInstitutionsFromServer();
    navigate(safeNext || "/");
  }, [navigate, safeNext]);

  // כניסה עם Google — יציב (useCallback) כדי לא לאתחל את כפתור גוגל שוב ושוב
  const handleGoogle = useCallback(
    async (credential) => {
      setSubmitError("");
      try {
        const auth = await loginWithGoogle(credential);
        // גם כניסת Google עוברת את השלב השני — אחרת היא הייתה דלת עוקפת
        if (auth?.twoFactorRequired) {
          setChallenge(auth);
          return;
        }
        await finishLogin();
      } catch (err) {
        if (err.message && err.message.includes("המנוי פג")) {
          navigate("/subscription-expired");
        } else {
          setSubmitError(err.message);
        }
      }
    },
    [navigate, finishLogin]
  );

  const handleGoogleError = useCallback(() => {
    setSubmitError("לא הצלחנו לטעון את כניסת Google. אפשר לנסות שוב או להשתמש בשם משתמש וסיסמה.");
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError("");
    const next = {};
    if (!usernameOrEmail.trim()) next.usernameOrEmail = "צריך למלא שם משתמש או מייל";
    if (!password) next.password = "צריך למלא סיסמה";
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const auth = await login({ usernameOrEmail: usernameOrEmail.trim(), password });
      if (auth?.twoFactorRequired) {
        setChallenge(auth);
        return;
      }
      await finishLogin();
    } catch (err) {
      // מנוי שפג — מסך ייעודי במקום הודעת שגיאה מבלבלת
      if (err.message && err.message.includes("המנוי פג")) {
        navigate("/subscription-expired");
      } else {
        setSubmitError(err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-bg" aria-hidden="true">
        <span className="login-blob login-blob--1" />
        <span className="login-blob login-blob--2" />
        <span className="login-blob login-blob--3" />
      </div>

      <div className="login-screen__content">
        <header className="login-hero">
          <h1 className="login-hero__brand">
            <BrandName />
          </h1>
          <p className="login-hero__tagline">ארגון חכם. ניהול מנצח</p>
        </header>

        <div className="login-card">
          {challenge ? (
            <TwoFactorPrompt
              challenge={challenge}
              onSuccess={finishLogin}
              onCancel={() => {
                setChallenge(null);
                setPassword("");
              }}
            />
          ) : (
          <>
          <h2 className="login-card__title">שמחים לראות אותך שוב 🙂</h2>
          <form onSubmit={handleSubmit} noValidate>
          <Input
            id="login-identifier"
            label="שם משתמש או מייל"
            autoComplete="username"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            error={errors.usernameOrEmail}
          />
          <div className="password-field">
            <Input
              id="login-password"
              label="סיסמה"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
            />
            <button
              type="button"
              className="password-field__eye"
              aria-label={showPassword ? "הסתרת סיסמה" : "הצגת סיסמה"}
              onClick={() => setShowPassword((s) => !s)}
            >
              👁
            </button>
          </div>
          {submitError && <ErrorMessage message={submitError} />}
          <div className="auth-page__actions">
            <Button type="submit" isLoading={isSubmitting}>
              כניסה
            </Button>
          </div>
          <p className="auth-page__hint">
            <Link to="/forgot-password">שכחת סיסמה? נשלח לך קוד למייל</Link>
          </p>
          <div className="auth-divider">או</div>
          <div className="google-signin-wrap">
            <GoogleSignInButton
              onCredential={handleGoogle}
              onError={handleGoogleError}
            />
          </div>
          <p className="auth-page__hint">
            עדיין אין לך חשבון? <Link to="/register">להרשמה מהירה</Link>
          </p>
          <p className="auth-page__hint">
            ספק/ית? <Link to="/supplier-login">לכניסת ספקים 🏷️</Link>
          </p>
          <ShareInvite
            url={window.location.origin}
            title="הזמנה ל-VaddyGo"
            message="מנהלים ועד הורים? VaddyGo עוזרת לנהל גבייה, תשלומים ותקשורת עם ההורים בקלות. מוזמנים להתחיל:"
          />
          </form>
          </>
          )}
        </div>
        <SupportLink />
      </div>
    </div>
  );
}

export default LoginPage;
